import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import { loadDashboardData } from '../utils/loadDashboardData';
import NewDashboardDialog from './dashboard-editor/NewDashboardDialog';

/** Choosing a dashboard opens it, from whichever page you are on. */
const DashboardSelector = ({ onChosen }) => {
  const navigate = useNavigate();
  const dashboards = useStore((state) => state.dashboards);
  const currentDashboard = useStore((state) => state.currentDashboard);
  const currentCompany = useStore((state) => state.currentCompany);

  const [isOpen, setIsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const user = useStore((state) => state.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDashboardChange = async (dashboard) => {
    setIsOpen(false);
    navigate('/');
    if (onChosen) onChosen();

    // Already loaded: going to the page is all that is needed.
    if (currentDashboard?.id === dashboard.id) {
      return;
    }

    try {
      // Load dashboard and metrics in parallel via shared utility
      await loadDashboardData({
        companyId: currentCompany?.id,
        dashboardId: dashboard.id,
        dashboards: dashboards, // already loaded, skip re-fetch
      });
    } catch (error) {
      console.error('Error switching dashboard:', error);
    }
  };

  // With no dashboards the dashboard page itself offers to create one.
  if (!dashboards || dashboards.length === 0) {
    return null;
  }

  return (
    <div className="dashboard-selector" ref={dropdownRef}>
      <button
        className="dashboard-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`Choose dashboard: ${currentDashboard?.name || 'none selected'}`}
      >
        <span className="selector-icon">📊</span>
        <span className="dashboard-name">
          {currentDashboard?.name || 'Select Dashboard'}
        </span>
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="dashboard-dropdown">
          {dashboards.map((dashboard) => (
            <button
              key={dashboard.id}
              className={`dashboard-option ${
                currentDashboard?.id === dashboard.id ? 'active' : ''
              }`}
              onClick={() => handleDashboardChange(dashboard)}
            >
              <span className="dashboard-option-name">{dashboard.name}</span>
              {currentDashboard?.id === dashboard.id && (
                <span className="checkmark">✓</span>
              )}
            </button>
          ))}
          {isAdmin && (
            <button type="button" className="dashboard-option dashboard-option--new" onClick={() => { setIsOpen(false); setCreating(true); }}>
              <span className="dashboard-option-name">+ New dashboard</span>
            </button>
          )}
        </div>
      )}
      {creating && <NewDashboardDialog onClose={() => setCreating(false)} />}
    </div>
  );
};

export default DashboardSelector;
